<template>
    <base-dialog :value="dialog" :title="title" :width="480" @input="onInput" @close="hide">
        <span class="members-sub" :class="{ 'members-sub--full': isFull }">{{ subtitle }}</span>

        <div v-if="members.length === 0" class="members-empty">{{ $t('business.no_members') }}</div>

        <div v-else class="members-list">
            <div v-for="member in members" :key="member.id" class="member-row">
                <span class="member-avatar" :style="avatarGradient(member.id)">{{ initials(member.name) }}</span>
                <div class="member-info">
                    <span class="member-name">{{ member.name || $t('business.no_value') }}</span>
                    <span class="member-email">{{ member.email || $t('business.no_email') }}</span>
                </div>
                <span class="member-kind" :class="{ 'member-kind--fixed': member.fixed }">
                    {{ member.fixed ? $t('business.booking_fixed_short') : $t('business.booking_once_short') }}
                </span>
                <button-tooltip top :tooltip="$t('business.remove_member')">
                    <template v-slot:activator="{ on, attrs }">
                        <button type="button" class="member-remove" v-on="on" v-bind="attrs"
                            :disabled="removing === member.id" @click="remove(member)">
                            <v-icon size="14">mdi-close</v-icon>
                        </button>
                    </template>
                </button-tooltip>
            </div>
        </div>

        <div class="members-add">
            <span v-if="isFull" class="members-add-hint">{{ $t('business.shift_full_hint') }}</span>
            <kimsa-combobox v-else :key="members.length" :placeholder="$t('business.add_member')" :items="candidates"
                :value="null" item-text="name" item-value="key" no-object dense solo searchable @change="add" />
        </div>

        <template v-slot:footer>
            <dialog-actions :cancel-label="$t('common.close')" :confirm-label="$t('business.edit_shift')"
                @cancel="hide" @confirm="$emit('edit', shift)" />
        </template>

        <booking-type-dialog ref="typeDialog" :subtitle="pendingName" @select="confirmAdd" />

        <kimsa-confirm ref="planConfirm" show-cancel :title="$t('business.plan_blocks_title')"
            :content="planWarning" :confirm-label="$t('business.add_anyway')" :persistent="false"
            @confirm="askBookingType" />
    </base-dialog>
</template>

<script>
import BaseDialog from "@/components/common/BaseDialog";
import DialogActions from "@/components/common/DialogActions";
import ButtonTooltip from "@/components/common/ButtonTooltip";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import BookingTypeDialog from "@/components/common/BookingTypeDialog";
import { capitalize, getAvatarGradient, getInitials } from "@/utils/Common";
import { isShiftFull, memberCount, memberKey, planAllowsShift, shiftMembers } from "@/utils/Business";

export default {
    name: "ShiftMembersDialog",
    components: { BaseDialog, DialogActions, ButtonTooltip, KimsaCombobox, KimsaConfirm, BookingTypeDialog },
    props: {
        shifts: {
            type: Array,
            default: () => [],
        },
        activities: {
            type: Array,
            default: () => [],
        },
        clients: {
            type: Array,
            default: () => [],
        },
        planTypes: {
            type: Array,
            default: () => [],
        },
        bookings: {
            type: Object,
            default: null,
        },
        removing: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            dialog: false,
            shiftId: null,
            pending: null,
        }
    },
    computed: {
        shift(vm = this) {
            return vm.shifts.find(item => item.id === vm.shiftId) || null
        },
        activity(vm = this) {
            return vm.activities.find(item => item.id === vm.shift?.activityId) || null
        },
        title(vm = this) {
            void vm.$i18n.locale
            if (!vm.shift) return ''
            const day = capitalize(vm.$moment().isoWeekday(vm.shift.day).format('dddd'))
            return `${vm.activity?.name || vm.$t('business.no_activity')} · ${day} ${vm.shift.startTime}`
        },
        emailByKey(vm = this) {
            const map = {}
            vm.clients.forEach(client => {
                const key = memberKey(client)
                if (key && client.email) map[key] = client.email
            })
            return map
        },
        members(vm = this) {
            return shiftMembers(vm.shift, vm.bookings).map(member => ({
                ...member,
                email: vm.emailByKey[member.id] || '',
            }))
        },
        isFull(vm = this) {
            return isShiftFull(vm.shift, vm.bookings)
        },
        subtitle(vm = this) {
            return vm.$t('business.seats_taken', {
                taken: memberCount(vm.shift, vm.bookings),
                total: vm.shift?.capacity || 0,
            })
        },
        candidates(vm = this) {
            const taken = new Set(vm.members.map(member => member.id))
            return vm.clients
                .filter(client => client.active && memberKey(client) && !taken.has(memberKey(client)))
                .map(client => ({ key: memberKey(client), name: client.name, email: client.email, client }))
                .sort((a, b) => a.name.localeCompare(b.name))
        },
        pendingName(vm = this) {
            return vm.pending?.name || ''
        },
        planWarning(vm = this) {
            if (!vm.pending) return ''
            return vm.$t('business.plan_blocks_member', { name: vm.pending.name })
        },
    },
    methods: {
        show(shift) {
            const vm = this
            vm.shiftId = shift?.id || null
            vm.dialog = true
        },
        hide() {
            this.dialog = false
        },
        onInput(value) {
            if (!value) this.hide()
        },
        initials(name) {
            return getInitials(name)
        },
        avatarGradient(id) {
            return getAvatarGradient(id)
        },
        add(key) {
            const vm = this
            const candidate = vm.candidates.find(item => item.key === key)
            if (!candidate || vm.isFull) return
            vm.pending = candidate

            const plan = vm.planTypes.find(item => item.id === candidate.client?.planId)
            const allowed = planAllowsShift(vm.shift, plan?.activities || [], candidate.client?.exceptions || [])
            if (allowed.allowed) return vm.askBookingType()
            vm.$refs.planConfirm?.show()
        },
        askBookingType() {
            this.$refs.typeDialog?.show()
        },
        confirmAdd(fixed) {
            const vm = this
            const candidate = vm.pending
            vm.pending = null
            if (!candidate) return
            vm.$emit('add', { shift: vm.shift, key: candidate.key, member: candidate, fixed })
        },
        remove(member) {
            this.$emit('remove', { shift: this.shift, key: member.id, fixed: member.fixed })
        },
    },
}
</script>

<style scoped>
.members-sub {
    display: block;
    margin-bottom: 12px;
    font-size: var(--fs-caption);
    color: var(--v-txtMuted-base);
}

.members-sub--full {
    color: var(--v-error-base);
    font-weight: 700;
}

.members-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 320px;
    overflow-y: auto;
}

.member-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 8px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
}

.member-avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    flex-shrink: 0;
    border-radius: 50%;
    font-size: 11px;
    font-weight: 700;
}

.member-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1 1 auto;
}

.member-name {
    font-size: var(--fs-label);
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.member-email {
    font-size: 11px;
    color: var(--v-txtMuted-base);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.member-kind {
    flex-shrink: 0;
    padding: 2px 8px;
    border-radius: 20px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    font-size: 10px;
    font-weight: 700;
    color: var(--v-txtMuted-base);
}

.member-kind--fixed {
    border-color: var(--v-success-base);
    color: var(--v-success-base);
}

.member-remove {
    width: 26px;
    height: 26px;
    flex-shrink: 0;
    border-radius: 7px;
    border: 1px solid var(--v-outline-base);
    color: var(--v-error-base);
    transition: border-color 0.15s, background 0.15s;
}

.member-remove:hover:not(:disabled) {
    border-color: var(--v-error-base);
    background: var(--v-surface-base);
}

.member-remove:disabled {
    opacity: 0.4;
}

.members-add {
    margin-top: 14px;
}

.members-add-hint {
    display: block;
    margin-top: 6px;
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.members-empty {
    padding: 24px 0;
    text-align: center;
    font-size: var(--fs-caption);
    color: var(--v-txtMuted-base);
}
</style>
